import * as React from 'react'

import { cn } from '@ps/ds/lib/utils'
import { type VariantProps, cva } from 'class-variance-authority'

const headingVariants = cva('font-brand text-heading', {
  variants: {
    size: {
      sm: 'text-sm',
      base: 'text-base',
      lg: 'text-lg',
      xl: 'text-xl',
      '2xl': 'text-2xl',
      '3xl': 'text-3xl',
      '4xl': 'text-4xl',
      '5xl': 'text-5xl',
      '6xl': 'text-6xl',
      '7xl': 'text-7xl',
    },
    weight: {
      semibold: 'font-semibold',
      bold: 'font-bold',
    },
    truncateLines: {
      '1': 'truncate',
      '2': 'line-clamp-2',
      '3': 'line-clamp-3',
    },
  },
  defaultVariants: {
    size: 'base',
    weight: 'semibold',
  },
})

export interface HeadingProps
  extends React.HTMLAttributes<HTMLHeadingElement>,
    VariantProps<typeof headingVariants> {
  /**
   * The semantic HTML element to render
   * @default 'h2'
   */
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
}

export const Heading = React.forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ className, size, weight, truncateLines, as: Component = 'h2', ...props }, ref) => {
    return (
      <Component
        ref={ref}
        className={cn(headingVariants({ size, weight, truncateLines }), className)}
        {...props}
      />
    )
  },
)

Heading.displayName = 'Heading'

type HeadingHelperTextProps = React.PropsWithChildren & { className?: string }

export const HeadingHelperText = ({ children, className }: HeadingHelperTextProps) => {
  return (
    <span className={cn('text-muted-foreground font-normal font-sans', className)}>{children}</span>
  )
}
